import * as validationRules from './validationRules';
import { vrMandatoryField, vrPhoneNumber } from './validationRules';
import intlTelInput, { Iti } from 'intl-tel-input';
import JustValidate from 'just-validate';
import { isAEMAuthorMode } from '@msc-b2c/shared-utils';

export const disableSuccessAndErrorMessage = (formElement: HTMLFormElement) => {
  const hideMessages = (containerSelector: string) => {
    const container = formElement.querySelector(containerSelector);
    if (container) {
      for (const msg of Array.from(container.children)) {
        msg.classList.add('hide');
      }
    }
  };

  hideMessages('.cmp-form__success-container');
  hideMessages('.cmp-form__error-container');
};

export function formChecks(formElement: HTMLFormElement, validator: JustValidate, event: Event) {
  if (isAEMAuthorMode()) return;

  if (formElement) {
    if (event) {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')
      ) {
        const id = target.id;
        const validationName = target.getAttribute('data-validation');
        const customValidationRule = target.getAttribute('data-customregex') ?? '';
        const value = target.value;
        const name = target.name;
        let isPhone = null;
        let phoneNumberErrorCount = 0;

        if (name.includes(':tel') && validationName?.includes('vr-phone-number')) {
          isPhone = intlTelInput.getInstance(target as HTMLInputElement) as Iti;
          const isMandatory = target.hasAttribute('required');
          const iti = isPhone;
          let isValid = true;
          if (value != '') {
            isValid = vrPhoneNumber(iti, 'vr-phone-number', id);
          } else if (isMandatory) {
            isValid = vrMandatoryField(isPhone.getNumber(), 'vr-mandatory-field', id, true);
          }

          validator.addField(formElement, [
            {
              validator: () => isValid,
            },
          ]);

          if (!isValid) {
            phoneNumberErrorCount++;
          }

          toggleClassError(id, !isValid, true);
        }
        const validationRulesArray = validationName?.split(',');
        if (validationRulesArray) {
          let errorCount = validateField(
            id,
            value,
            validationRulesArray,
            customValidationRule,
            formElement,
            isPhone
          );

          // Aggiunge il campo dinamicamente a JustValidate
          validator.addField(`#${id}`, [
            {
              validator: () => errorCount === 0, // Se non ci sono errori
            },
          ]);

          if (isPhone) {
            errorCount = errorCount + phoneNumberErrorCount;
            toggleClassError(id, errorCount > 0, true);
          } else {
            toggleClassError(id, errorCount > 0);
          }
        }
      }
    }
  }
}

// Function to validate each single field
export function validateField(
  id: string,
  value: string,
  validationRulesArray: string[],
  customValidationRule: string,
  formElement: HTMLFormElement,
  isPhone?: Iti | null
): number {
  let errorCount = 0;
  let regexArray: string[] = [];
  let regexIndex = 0;
  let currentRegex = '';

  for (const item of validationRulesArray) {
    let firstNameInput = null;
    let lastNameInput = null;
    let confirmEmailInput = null;
    let email = null;

    switch (item) {
      case 'vr-mandatory-field':
        if (isPhone) {
          !validationRules.vrMandatoryField(isPhone.getNumber(), 'vr-mandatory-field', id, true) &&
            errorCount++;
        } else {
          !validationRules.vrMandatoryField(value, item, id) && errorCount++;
        }
        break;
      case 'vr-custom-regex':
        if (regexArray.length === 0) {
          regexArray = customValidationRule.split('|').filter((item) => item.trim() !== '');
          regexIndex = 0;
        }
        currentRegex = regexArray[regexIndex];
        if (!validationRules.vrCustomRegex(value, item, id, currentRegex, regexIndex, errorCount)) {
          errorCount++;
        }
        regexIndex++;
        if (regexIndex >= regexArray.length) {
          regexIndex = 0;
        }
        break;
      case 'vr-alphabetical-spaces-specialcharacters':
        !validationRules.vrAlphabeticalSpacesSpecialcharacters(value, item, id) && errorCount++;
        break;
      case 'vr-not-special-or-emojis':
        !validationRules.vrNoSpecialCharactersOrEmojis(value, item, id) && errorCount++;
        break;
      case 'vr-min-2-characters-max-50-characters':
        !validationRules.vrMin2CharactersMax50characters(value, item, id) && errorCount++;
        break;
      case 'vr-min-1-characters-max-50-characters':
        !validationRules.vrMin1CharactersMax50characters(value, item, id) && errorCount++;
        break;
      case 'vr-signature-surname-name':
        firstNameInput = formElement.querySelector(
          'input[name="customer.name"]'
        ) as HTMLInputElement;
        lastNameInput = formElement.querySelector(
          'input[name="customer.surname"]'
        ) as HTMLInputElement;
        !validationRules.vrSignatureSurnameName(
          value,
          firstNameInput.value,
          lastNameInput.value,
          item,
          id
        ) && errorCount++;
        break;
      case 'vr-signature-name':
        firstNameInput = formElement.querySelector(
          'input[name="guest.firstName"]'
        ) as HTMLInputElement;
        !validationRules.vrSignatureExactMatch(value, firstNameInput.value, item, id) &&
          errorCount++;
        break;
      case 'vr-signature-surname':
        lastNameInput = formElement.querySelector(
          'input[name="guest.lastName"]'
        ) as HTMLInputElement;
        !validationRules.vrSignatureExactMatch(value, lastNameInput.value, item, id) &&
          errorCount++;
        break;
      case 'vr-only-characters-and-numbers':
        !validationRules.vrOnlyCharactersAndNumber(value, item, id) && errorCount++;
        break;
      case 'vr-max-100-characters':
        !validationRules.vrMax100Characters(value, item, id) && errorCount++;
        break;
      case 'vr-only-numeric-min-3-digit':
        !validationRules.vrOnlyNumericMin3Digit(value, item, id) && errorCount++;
        break;
      case 'vr-postalcode-countrys-standard':
        !validationRules.vrPostalcodeCountrysStandard(item, id) && errorCount++;
        break;
      case 'vr-future-date-control-and-format':
        !validationRules.vrFutureDateControlAndFormat(value, item, id) && errorCount++;
        break;
      case 'vr-email-check-two-letter-mail-and-confirm-email':
        !validationRules.vrEmailCheckTwoLetterMailAndConfirmEmail(value, item, id) && errorCount++;
        confirmEmailInput = formElement.querySelector(
          'input[name="customer.confirmEmail_skip"]'
        ) as HTMLInputElement;
        if (confirmEmailInput && confirmEmailInput.value !== '') {
          const confirmEmail = formElement.querySelector(
            'input[name="customer.confirmEmail_skip"]'
          ) as HTMLInputElement;
          const checkValidate = !validationRules.vrValidateConfirmEmail(
            confirmEmail.value,
            value,
            'vr-validate-confirm-email',
            confirmEmail.id
          );
          if (!checkValidate) {
            toggleClassError(confirmEmail.id, false, false);
          } else {
            toggleClassError(confirmEmail.id, true, false);
          }
        }
        break;
      case 'vr-email-check-one-letter-mail-and-confirm-email':
        !validationRules.vrEmailCheckOneLetterMailAndConfirmEmail(value, item, id) && errorCount++;
        confirmEmailInput = formElement.querySelector(
          'input[name="customer.confirmEmail_skip"]'
        ) as HTMLInputElement;
        if (confirmEmailInput && confirmEmailInput.value !== '') {
          const confirmEmail = formElement.querySelector(
            'input[name="customer.confirmEmail_skip"]'
          ) as HTMLInputElement;
          const checkValidate = !validationRules.vrValidateConfirmEmail(
            confirmEmail.value,
            value,
            'vr-validate-confirm-email',
            confirmEmail.id
          );
          if (!checkValidate) {
            toggleClassError(confirmEmail.id, false, false);
          } else {
            toggleClassError(confirmEmail.id, true, false);
          }
        }
        break;
      case 'vr-validate-confirm-email':
        email = formElement.querySelector('input[name="customer.email"]') as HTMLInputElement;
        !validationRules.vrValidateConfirmEmail(email.value, value, item, id) && errorCount++;
        break;
      case 'vr-min-6-digit-and-max-13-digit':
        !validationRules.vMin6DigitAndMax13Digit(value, item, id) && errorCount++;
        break;
      case 'vr-alphabetical-spaces-specialcharacters-number':
        !validationRules.vrAlphabeticalSpacesSpecialcharactersNumber(value, item, id) &&
          errorCount++;
        break;
      case 'vr-max-500-characters':
        !validationRules.vrMax500Characters(value, item, id) && errorCount++;
        break;
      case 'vr-date-no-before-01011990':
        !validationRules.vrDateNoBefore01011990(value, item, id) && errorCount++;
        break;
      case 'vr-date-past-date':
        !validationRules.vrDatePastDate(value, item, id) && errorCount++;
        break;
      default:
        break;
    }
  }

  return errorCount;
}

if (!isAEMAuthorMode()) {
  document.querySelectorAll('input:not(.react-input), textarea').forEach((element) => {
    element.addEventListener('focus', (event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      const id = target.id;
      toggleClassError(id, false, target.name?.includes(':tel'));

      const errorLabel = document.querySelector(`.error-label`);
      if (errorLabel) {
        (errorLabel as HTMLElement).style.display = 'none';
      }

      const errorListValidation =
        target?.parentElement?.parentElement?.querySelector('.error-list-validation');
      const hideErrorMessage = 'input-hidden-error-messagge-vr';
      if (errorListValidation) {
        for (let i = 0; i < errorListValidation.children.length; i++) {
          const child = errorListValidation.children[i];
          child.classList.add(hideErrorMessage);
        }
      }
    });
  });

  document.addEventListener('input:not(.react-input)', function (event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const id = target.id;

    if (target.name?.includes(':tel')) {
      if (target.value.length > 13) {
        target.value = target.value.slice(0, 13);
      }
    }

    toggleClassError(id, false, target.name?.includes(':tel'));
    const errorLabel = document.querySelector(`.error-label`);
    if (errorLabel) {
      (errorLabel as HTMLElement).style.display = 'none';
    }
  });
}

export function toggleClassError(id: string, add = true, isPhone = false) {
  const element = document.getElementById(id);
  const action = add ? 'add' : 'remove';
  const targetElement = isPhone
    ? element?.parentElement?.parentElement?.parentElement
    : element?.parentElement?.parentElement;
  targetElement?.classList[action]('error-input');
}

if (!isAEMAuthorMode()) {
  document.addEventListener('DOMContentLoaded', function () {
    const inputField = document.querySelector('input[data-no-copy-paste="true"]');
    if (inputField) {
      inputField.addEventListener('copy', function (e) {
        e.preventDefault();
      });
      inputField.addEventListener('cut', function (e) {
        e.preventDefault();
      });
      inputField.addEventListener('paste', function (e) {
        e.preventDefault();
      });
    }
  });
}
